import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Ban, CheckCircle2, KeyRound, History, Pencil, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";
import { fmtDate } from "./Dashboard";
import { ROLE_BADGE_COLORS } from "../lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const EMPTY_FORM = { username: "", email: "", first_name: "", last_name: "", role: "AGENT", service: "", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [history, setHistory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await API.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Compte ${deleteTarget.username} supprime.`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback((p = page, ps = pageSize, query = q) => {
    setLoading(true);
    API.get("/admin/users", { params: { q: query, page: p, page_size: ps } })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, q]);

  useEffect(() => {
    load(1);
    API.get("/admin/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setTempPassword(null); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({ username: u.username, first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role, service: u.service || "" });
    setTempPassword(null);
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await API.put(`/admin/users/${editing}`, form);
        toast.success("Utilisateur mis a jour.");
        setDialogOpen(false);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        const r = await API.post("/admin/users", payload);
        setTempPassword(r.data.temporary_password);
        toast.success("Utilisateur cree.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const action = async (u, kind) => {
    try {
      if (kind === "suspend") {
        await API.post(`/admin/users/${u.id}/suspend`);
        toast.success(`${u.username} suspendu.`);
      } else if (kind === "reactivate") {
        await API.post(`/admin/users/${u.id}/reactivate`);
        toast.success(`${u.username} reactive.`);
      } else if (kind === "reset") {
        const r = await API.post(`/admin/users/${u.id}/reset-password`);
        setTempPassword(r.data.temporary_password);
        setEditing(u.id);
        setForm({ username: u.username, first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role, service: u.service || "" });
        setDialogOpen(true);
        toast.success("Mot de passe reinitialise.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openHistory = (u) => {
    setHistoryUser(u);
    setHistory(null);
    API.get(`/admin/users/${u.id}/history`).then((r) => setHistory(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Administration des utilisateurs</h1>
          <p className="text-sm text-muted-foreground">Les comptes sont crees exclusivement par un administrateur habilite. Aucune inscription publique.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="user-create">
          <Plus className="h-4 w-4 mr-1" /> Creer un compte
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)}
          placeholder="Nom, identifiant, email..." className="pl-9" data-testid="users-search" />
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Nom", "Prenom", "Identifiant", "Role", "Service", "Statut", "Derniere connexion", "Cree le", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((u, i) => (
                  <tr key={u.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`user-row-${i}`}>
                    <td className="px-3 py-2.5 font-medium">{u.last_name}</td>
                    <td className="px-3 py-2.5">{u.first_name}</td>
                    <td className="px-3 py-2.5 mono text-xs">{u.username}</td>
                    <td className="px-3 py-2.5"><Badge className={`text-[10px] ${ROLE_BADGE_COLORS[u.role] || ""}`}>{u.role}</Badge></td>
                    <td className="px-3 py-2.5 text-xs max-w-40 truncate">{u.service || "-"}</td>
                    <td className="px-3 py-2.5">
                      <Badge className={u.status === "actif"
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"} data-testid={`user-status-${i}`}>
                        {u.status === "actif" ? "Actif" : "Suspendu"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs mono">{u.last_login_at ? fmtDate(u.last_login_at) : "Jamais"}</td>
                    <td className="px-3 py-2.5 text-xs mono">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Button variant="ghost" size="sm" title="Modifier" onClick={() => openEdit(u)} data-testid={`user-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" title="Reinitialiser le mot de passe" onClick={() => action(u, "reset")} data-testid={`user-reset-${i}`}><KeyRound className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" title="Historique" onClick={() => openHistory(u)} data-testid={`user-history-${i}`}><History className="h-4 w-4" /></Button>
                      {u.id !== me?.id && (
                        u.status === "actif" ? (
                          <Button variant="ghost" size="sm" title="Suspendre" className="text-red-600" onClick={() => action(u, "suspend")} data-testid={`user-suspend-${i}`}><Ban className="h-4 w-4" /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" title="Reactiver" className="text-emerald-600" onClick={() => action(u, "reactivate")} data-testid={`user-reactivate-${i}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        )
                      )}
                      {u.id !== me?.id && (
                        <Button variant="ghost" size="sm" title="Supprimer" className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setDeleteTarget(u)} data-testid={`user-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="users-pagination" />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier l'utilisateur" : "Creer un compte utilisateur"}</DialogTitle>
          </DialogHeader>
          {tempPassword && (
            <div className="rounded-lg border border-[#FCD116] bg-amber-50 dark:bg-amber-900/20 px-4 py-3" data-testid="temp-password-box">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">Mot de passe temporaire (a communiquer une seule fois)</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="mono text-lg font-bold" data-testid="temp-password-value">{tempPassword}</code>
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Copie."); }} data-testid="temp-password-copy">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">L'utilisateur devra le changer a sa premiere connexion.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Identifiant *</Label>
              <Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-form-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nom *</Label>
              <Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="user-form-lastname" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prenom *</Label>
              <Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="user-form-firstname" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email *</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Service</Label>
              <Input value={form.service || ""} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="user-form-service" />
            </div>
            {!editing && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mot de passe temporaire (optionnel — genere automatiquement si vide)</Label>
                <Input value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-cancel">Fermer</Button>
            <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="user-save">
              {saving ? "Enregistrement..." : editing ? "Enregistrer" : "Creer le compte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Supprimer le compte</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Vous etes sur le point de supprimer definitivement le compte de{" "}
            <b>{deleteTarget?.first_name} {deleteTarget?.last_name}</b> ({deleteTarget?.username}).
            Cette action est irreversible et sera consignee dans le journal d'audit.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="user-delete-cancel">Annuler</Button>
            <Button onClick={doDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="user-delete-confirm">
              {deleting ? "Suppression..." : "Supprimer definitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!historyUser} onOpenChange={(o) => !o && setHistoryUser(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="user-history-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">Historique — {historyUser?.first_name} {historyUser?.last_name}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {!history ? <Skeleton className="h-40 w-full" /> : history.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune activite enregistree.</p>
            ) : history.items.map((a) => (
              <div key={a.id} className="rounded-lg border px-3 py-2 text-sm" data-testid={`history-item-${a.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{a.action}</span>
                  <span className="text-[11px] text-muted-foreground mono">{fmtDate(a.ts)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{a.resource_type || ""} {a.ip ? `— IP ${a.ip}` : ""}</p>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
